import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Col, Container, Row } from 'react-bootstrap';
import { SIZE } from 'baseui/button';
import { ALIGN, Radio, RadioGroup } from 'baseui/radio';
import { Controller, useForm } from 'react-hook-form';
import store from 'store';
import { paths } from 'frameworks_drivers/routes';
import { TrackerUtil } from 'tracking/TrackerUtil';
import TitleWithPrevPageLink from 'presentation/components/TitleWithPrevPageLink';
import { WizardSteps, WizardStepsContainer } from 'presentation/components/WizardSteps';
import PageDescription from 'presentation/components/PageDescription';
import { FormValidator } from 'tools/FormValidator';
import { Button } from '@techem/ui-components';
import { RadioOptionKeys, translationKeyForRadioOption } from 'tools/RadioOptionMapper';
import { setContactDetails } from 'frameworks_drivers/redux/reducers/onboardingInformation/onboardingInformationSlice';
import Input from 'presentation/components/Input';
import FormControl from 'presentation/components/FormControl';
import { useNavigate } from 'react-router-dom';
import { DISABLE_FLOW_DEEP_LINKING_PROTECTION } from 'config';
import useAutoFocus from 'tools/hooks/useAutoFocus';

interface FormData {
  gender: string;
  fname: string;
  lname: string;
  phone: string;
  email: string;
}

const ContactDetailsPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const firstnameInputRef = useAutoFocus();

  const {
    control,
    handleSubmit,
    formState: { errors }
  } = useForm<FormData>({
    defaultValues: store.getState().onboardingInfo.contactDetails
  });

  const prevPath = paths.Offer;

  useEffect(() => {
    if (
      !DISABLE_FLOW_DEEP_LINKING_PROTECTION &&
      store.getState().onboardingInfo.selectedProducts.length === 0
    ) {
      navigate(prevPath, { replace: true });
    }
  }, [prevPath, navigate]);

  const handlePrevBtnClicked = () => {
    TrackerUtil.trackPrevBtnClick();
    navigate(prevPath);
  };

  const onSubmit = handleSubmit(({ gender, fname, lname, phone }, e) => {
    const email = store.getState().onboardingInfo.contactDetails.email;
    store.dispatch(
      setContactDetails({
        gender,
        fname,
        lname,
        phone,
        email
      })
    );

    TrackerUtil.trackNextBtnClick();
    navigate(paths.InvoiceAddress);

    e?.preventDefault();
  });

  return (
    <Container className="pb-4 mb-5">
      <WizardStepsContainer>
        <WizardSteps currentStep={1} totalSteps={4} />
      </WizardStepsContainer>
      <TitleWithPrevPageLink
        onPrevClick={handlePrevBtnClicked}
        title={t('pageContactDetailsTitle')}
      />
      <PageDescription indented>{t('pageContactDetailsDescription')}</PageDescription>

      <form noValidate onSubmit={onSubmit}>
        <Row className="justify-content-center mt-lg-4 pt-2">
          <Col xs={10} lg={6}>
            <fieldset>
              <legend id="gender-label" className="visually-hidden">
                {t('pageContactDetailsLabelGender')}
              </legend>
              <FormControl label={null} error={errors.gender && t('pageContactDetailsErrorGender')}>
                <Controller
                  name="gender"
                  control={control}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <RadioGroup
                      value={field.value}
                      name={field.name}
                      onChange={(e) => field.onChange(e.target.value)}
                      align={ALIGN.horizontal}
                      aria-labelledby="gender-label"
                    >
                      <Radio
                        value={RadioOptionKeys.ContactOptions.Female}
                        checked={field.value === RadioOptionKeys.ContactOptions.Female}
                        overrides={{
                          Label: () => (
                            <span className="ms-2 me-5">
                              {t(
                                translationKeyForRadioOption(RadioOptionKeys.ContactOptions.Female)
                              )}
                            </span>
                          )
                        }}
                      ></Radio>
                      <Radio
                        value={RadioOptionKeys.ContactOptions.Male}
                        checked={field.value === RadioOptionKeys.ContactOptions.Male}
                        overrides={{
                          Label: () => (
                            <span className="ms-2 me-5">
                              {t(translationKeyForRadioOption(RadioOptionKeys.ContactOptions.Male))}
                            </span>
                          )
                        }}
                      ></Radio>
                    </RadioGroup>
                  )}
                />
              </FormControl>
            </fieldset>
          </Col>
        </Row>

        <Row className="justify-content-center mt-lg-4">
          <Col xs={10} lg={3}>
            <FormControl
              required
              label={<span id="fname-label">{t('pageContactDetailsLabelFirstName')}</span>}
              error={errors.fname && t('pageContactDetailsErrorFirstName')}
            >
              <Controller
                name="fname"
                control={control}
                rules={{
                  required: true,
                  validate: {
                    validate: (v) => FormValidator.isValidFirstOrLastName(v)
                  }
                }}
                render={({ field: { onChange, value } }) => (
                  <Input
                    id="field-fname"
                    aria-labelledby="fname-label"
                    type="text"
                    size={SIZE.default}
                    error={!!errors.fname}
                    value={value}
                    onChange={onChange}
                    autoComplete="given-name"
                    additionalTestId="firstName"
                    inputRef={firstnameInputRef}
                  />
                )}
              />
            </FormControl>
          </Col>
          <Col xs={10} lg={3}>
            <FormControl
              required
              label={<span id="lname-label">{t('pageContactDetailsLabelLastName')}</span>}
              error={errors.lname && t('pageContactDetailsErrorLastName')}
            >
              <Controller
                name="lname"
                control={control}
                rules={{
                  required: true,
                  validate: {
                    validate: (v) => FormValidator.isValidFirstOrLastName(v)
                  }
                }}
                render={({ field: { onChange, value } }) => (
                  <Input
                    id="field-lname"
                    aria-labelledby="lname-label"
                    type={'text'}
                    size={SIZE.default}
                    error={!!errors.lname}
                    value={value}
                    onChange={onChange}
                    autoComplete="family-name"
                    additionalTestId="lastName"
                  />
                )}
              />
            </FormControl>
          </Col>
        </Row>

        <Row className="justify-content-center mt-lg-4">
          <Col xs={10} lg={3}>
            <FormControl
              required
              label={<span id="phone-label">{t('pageContactDetailsLabelPhoneNumber')}</span>}
              error={errors.phone && t('pageContactDetailsErrorPhoneNumber')}
            >
              <Controller
                control={control}
                rules={{
                  required: true,
                  validate: {
                    validate: (v) => FormValidator.isValidPhoneNumber(v)
                  }
                }}
                render={({ field: { onChange, value } }) => (
                  <Input
                    id="field-phone"
                    aria-labelledby="phone-label"
                    type={'tel'}
                    size={SIZE.default}
                    error={!!errors.phone}
                    value={value}
                    onChange={onChange}
                    autoComplete="tel"
                    additionalTestId="phoneNumber"
                  />
                )}
                name="phone"
              />
            </FormControl>
          </Col>
          <Col xs={10} lg={3}>
            <FormControl
              required
              label={<span id="email-label">{t('pageContactDetailsLabelEMailAddress')}</span>}
            >
              <Controller
                control={control}
                rules={{
                  required: true
                }}
                render={({ field: { onChange, value } }) => (
                  <Input
                    id="field-email"
                    aria-labelledby="email-label"
                    size={SIZE.default}
                    type="email"
                    disabled
                    value={value}
                    onChange={onChange}
                    autoComplete="email"
                    additionalTestId="emailAddress"
                  />
                )}
                name="email"
              />
            </FormControl>
          </Col>
        </Row>

        <Row className="justify-content-center py-5">
          <Col className="text-center col-auto">
            <Button size={SIZE.large} type="submit" additionalTestId="next">
              {t('nextBtnText')}
            </Button>
          </Col>
        </Row>
      </form>
    </Container>
  );
};

export default ContactDetailsPage;
